import PropTypes from "prop-types";
import React, { Component } from "react";
import { Alert, Modal } from "react-bootstrap";
import { FormattedMessage, injectIntl } from "react-intl";
import { ConfirmModal } from "../../../common/confirmModal";
import ModalDialogUtil from "../../../utils/ModalDialogUtil";
import { DAADLogo } from "../../app/DAADLogo";
import SurveyFooterContainer from "../surveyView/SurveyFooterContainer";
import SurveyViewContainer from "../surveyView/SurveyViewContainer";

const INTERVAL_IN_MILLISECONDS = 60000;
const SAVING_INTERVAL_IN_MILLISECONDS = 180000;

/**
 * The component wraps SurveyRunner(Surveyjs).
 */
class SurveyRunner extends Component {
  constructor(props) {
    super(props);
    this.close = this.close.bind(this);
    this.closeRunner = this.closeRunner.bind(this);
    this.saveSurveyResult = this.saveSurveyResult.bind(this);
    this.state = {
      readyToLeave: false,
      showConfirmModal: false
    };
    this.bodyRef = React.createRef();
    this.handlePageChange = this.handlePageChange.bind(this);

    // Internal interval holders (not part of state)
    this.editingInterval = null;
    this.savingInterval = null;
  }

  isDisabled() {
    const { readonly, readOnlyByPermissions, jwtExpired } = this.props;
    return readonly || readOnlyByPermissions || jwtExpired;
  }

  componentDidMount() {
    const isDisabled = this.isDisabled();

    if (!isDisabled) {
      this.editingInterval = setInterval(() => {
        this.props.extendLockEditingOfSurvey();
      }, INTERVAL_IN_MILLISECONDS);

      this.savingInterval  = setInterval(() => {
        if (this.props.surveyResultChanged) {
          this.props.saveSurveyResult(null, true);
        }
      }, SAVING_INTERVAL_IN_MILLISECONDS);
    }
  }

  componentWillUnmount() {
    this.clearIntervals();
    this.setState({ readyToLeave: true });
  }

  clearIntervals() {
    if (this.editingInterval) clearInterval(this.editingInterval);
    if (this.savingInterval) clearInterval(this.savingInterval);
    this.editingInterval = null;
    this.savingInterval = null;
  }

  handlePageChange() {
    this.bodyRef.current?.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth"
    });
  }

  saveSurveyResult(survey) {
    this.setState({ readyToLeave: true });
    ModalDialogUtil.closeModalDialogBodyFix();
    this.props.saveSurveyResult?.(survey);
  }

  close() {
    if (!this.isDisabled() && !this.state.readyToLeave) {
      this.setState({
        showConfirmModal: true
      });
    } else {
      this.closeRunner();
    }
  }

  closeRunner() {
   this.clearIntervals();
    this.props.hideRunner?.();
  }

  // renders main pop-up modal which represents running survey
  renderSurveyRunnerModal() {
    const {
      intl,
      surveyName,
      surveyFundingArea,
      showDialog,
      surveyProgram,
      surveyProject,
      surveyTemplate,
      projectName,
      readOnlyByPermissions,
      projectForeignId,
      currentOverview,
      jwtExpired
    } = this.props;

    if (this.isDisabled()) this.clearIntervals();
    const confirmLabel = intl.formatMessage({ id: "user.surveyView.saveResult" });

    return (
      <Modal show className="modal-full" scrollable onHide={this.close}>
        <Modal.Header className="bg-light" closeButton>
          <Modal.Title>
            <DAADLogo />
            {projectForeignId ? projectForeignId + " " : ""}
            {projectName ? projectName : surveyName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={!this.isDisabled() ? "" : "editing-disabled"} ref={this.bodyRef}>
          {this.isDisabled() && (
            <Alert variant={jwtExpired ? "danger" : readOnlyByPermissions ? "warning" : "info"}>
              {readOnlyByPermissions ? (
                <FormattedMessage id="user.surveyView.editing.permission.disabled" />
              ) : jwtExpired ? (
                <FormattedMessage id="heartbeat.jwt.verification" />
              ) : (
                <FormattedMessage id="user.surveyView.editing.disabled" />
              )}
            </Alert>
          )}
          <SurveyViewContainer
            onPageChange={this.handlePageChange}
            surveyName={surveyName}
            surveyFundingArea={surveyFundingArea}
            surveyProgram={surveyProgram}
            surveyProject={surveyProject}
            surveyTemplate={surveyTemplate}
            roles={currentOverview.roles}
          />
        </Modal.Body>
        <Modal.Footer className="bg-dark text-light">
          <SurveyFooterContainer
            onConfirm={this.saveSurveyResult}
            confirmLabel={confirmLabel}
            surveyTemplate={surveyTemplate}
            showDialog={showDialog}
          />
        </Modal.Footer>
      </Modal>
    );
  }

  render() {
    const { intl } = this.props;
    return (
      <div id="surveyBuilder" className="modal-container">
        <ConfirmModal
          intl={intl}
          message={intl.formatMessage({ id: "dialog.leave.message" })}
          onConfirm={this.closeRunner}
          handleClose={() => this.setState({
            showConfirmModal: false
          })}
          show={this.state.showConfirmModal}
        />
        {this.renderSurveyRunnerModal()}
      </div>
    );
  }
}

SurveyRunner.propTypes = {
  intl: PropTypes.object,
  surveyName: PropTypes.string,
  projectForeignId: PropTypes.string,
  surveyFundingArea: PropTypes.object,
  surveyProgram: PropTypes.object,
  surveyProject: PropTypes.object,
  currentOverview: PropTypes.object,
  surveyTemplate: PropTypes.object,
  projectName: PropTypes.string,
  saveSurveyResult: PropTypes.func,
  hideRunner: PropTypes.func,
  showDialog: PropTypes.func,
  readonly: PropTypes.bool,
  readOnlyByPermissions: PropTypes.bool,
  jwtExpired: PropTypes.bool,
  surveyResultChanged: PropTypes.bool,
  extendLockEditingOfSurvey: PropTypes.func
};

export default injectIntl(SurveyRunner);
